import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import {
  setBackEvent,
  setDefaultBackEvent,
} from '@kakaopay-common/payweb-bridge';
import { Icon, Typography } from '@kakaopay-web-ui/components';

import useThrottleScrollCallback from 'src/templates/hooks/useThrottleScrollCallback';

import TitleBar, {
  TitleBarButton,
  TitleBarCenter,
  TitleBarLeft,
  TitleBarRight,
} from '../TitleBar';
import { useIsPayWebview } from '~/hooks/useIsPayWebview';
import { useQueryString } from '~/hooks/useQueryString';
import { isAndroid } from '~/utils/appInfo';
import { dynamicHistoryBack } from '~/utils/dynamicHistoryBack';

import './style.scss';

type Props = {
  title?: string;
  isTitleVisible?: boolean;
  isInitNonDisplay?: boolean;
  useShareButton?: boolean;
  useScrollBorder?: boolean;
  useScrollTitleToggle?: boolean;
  handleShare?: VoidFunction;
};

const Header = ({
  title = '',
  isTitleVisible = false,
  isInitNonDisplay = false,
  useShareButton = false,
  useScrollBorder = false,
  useScrollTitleToggle = false,
  handleShare = () => undefined,
}: Props) => {
  const { openDiscovery = false } = useQueryString<{ openDiscovery?: boolean }>(
    {
      convertToCamelcase: true,
    }
  );

  const isPayWebview = useIsPayWebview();
  const [showBorder, setShowBorder] = useState(false);
  const [showTitleAtScroll, setShowTitleAtScroll] = useState(false);

  const handleScroll = useCallback(() => {
    // 상단 화면 스크롤시 show border
    setShowBorder(useScrollBorder && window.scrollY > 40);
    // 상단 화면 스크롤시 show title
    setShowTitleAtScroll(useScrollTitleToggle && window.scrollY > 90);
  }, [useScrollBorder, useScrollTitleToggle]);

  useThrottleScrollCallback({
    callback: () => handleScroll(),
  });

  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const historyBack = useCallback(() => {
    if (!isPayWebview) {
      return;
    }

    dynamicHistoryBack({
      shouldOpenDiscoveryTab: openDiscovery,
    });
  }, [isPayWebview, openDiscovery]);

  useEffect(() => {
    if (isPayWebview && isAndroid) {
      // 안드로이드의 하단 디바이스 back키를 눌렀을 때 동작 제어
      setBackEvent(() => historyBack());

      return () => {
        setDefaultBackEvent();
      };
    }
  }, [historyBack, isPayWebview]);

  // [외부 브라우저 + 상단 배너 표시] 케이스에서 스크롤 최상단인 경우 헤더바 표시X
  if (!isPayWebview && isInitNonDisplay && !showTitleAtScroll) {
    return <></>;
  }

  return (
    <>
      <TitleBar contentAlignLeft={false} useBorder={showBorder}>
        {isPayWebview && (
          <TitleBarLeft>
            <TitleBarButton onClick={historyBack} ariaLabel="뒤로가기">
              <Icon icon="NaviArrowBack" size={24} colorTheme="high-emphasis" />
            </TitleBarButton>
          </TitleBarLeft>
        )}
        <TitleBarCenter>
          <Typography
            type="subtitle18"
            tag="h1"
            weight="bold"
            color="high-emphasis"
            className={classNames('Header__title', {
              'Header__title-show': isTitleVisible || showTitleAtScroll,
            })}
          >
            {title?.replaceAll('<br/>', ' ')}
          </Typography>
        </TitleBarCenter>
        {useShareButton && (
          <TitleBarRight>
            <TitleBarButton onClick={handleShare}>
              <Typography
                type="body15"
                tag="span"
                weight="regular"
                color="high-emphasis"
              >
                공유하기
              </Typography>
            </TitleBarButton>
          </TitleBarRight>
        )}
      </TitleBar>
    </>
  );
};
export default Header;
